import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { GetDeliveryEndpointsByIntegratorResponse } from "../../../common/api/orders-api/delivery-endpoint/GetDeliveryEndpointsByIntegratorResponse";
import { DeliveryEndpointType } from "../../../common/types/DeliveryEndpointType";
import { useAdminDeliveryEndpointApi } from "../api/useAdminDeliveryEndpointApi";
import { DeliveryEndpointItem, mapToDeliveryEndpointItem } from "../listing/DeliveryEndpointItem";
import { DeliveryEndpointContext } from "./DeliveryEndpointContext";

interface DeliveryEndpointProviderProps {
    children: React.ReactNode;
}

export const DeliveryEndpointProvider: React.FC<DeliveryEndpointProviderProps> = (
    props: DeliveryEndpointProviderProps
) => {
    const { children } = props;
    const { getDeliveryEndpointsByIntegrator } = useAdminDeliveryEndpointApi();
    const { integratorId } = useParams<{ integratorId: string }>();

    const [deliveryEndpointItems, setDeliveryEndpointItems] = useState<DeliveryEndpointItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        setIsLoading(true);

        getDeliveryEndpointsByIntegrator({ integratorId })
            .then((response: GetDeliveryEndpointsByIntegratorResponse) => {
                const deliveryEndpoints = response.items.map(mapToDeliveryEndpointItem);

                setDeliveryEndpointItems(deliveryEndpoints);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const updateSmartjogDeliveryEndpointItem = (listingItem: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevItems) =>
            prevItems.map((item) =>
                item.id === listingItem.id
                    ? {
                          ...item,
                          name: listingItem.name,
                          details: listingItem.details,
                          updatedDate: new Date().toISOString(),
                          isModified: true
                      }
                    : item
            )
        );
    };

    const addSmartjogDeliveryEndpointItem = (item: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevState) => [
            ...prevState,
            {
                id: item.id,
                name: item.name,
                type: DeliveryEndpointType.Smartjog,
                details: item.details,
                updatedDate: new Date().toISOString(),
                isModified: true
            }
        ]);
    };

    const updateAsperaDeliveryEndpointItem = (listingItem: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevItems) =>
            prevItems.map((item) =>
                item.id === listingItem.id
                    ? {
                          ...item,
                          name: item.name,
                          details: listingItem.details,
                          updatedDate: new Date().toISOString(),
                          isModified: true
                      }
                    : item
            )
        );
    };

    const addAsperaDeliveryEndpointItem = (item: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevState) => [
            ...prevState,
            {
                id: item.id,
                name: item.name,
                type: DeliveryEndpointType.Aspera,
                details: item.details,
                updatedDate: new Date().toISOString(),
                isModified: true
            }
        ]);
    };

    const updateSigniantDeliveryEndpointItem = (listingItem: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevItems) =>
            prevItems.map((item) =>
                listingItem.id === item.id
                    ? {
                          ...item,
                          name: listingItem.name,
                          details: listingItem.details,
                          updatedDate: new Date().toISOString(),
                          isModified: true
                      }
                    : item
            )
        );
    };

    const addSigniantDeliveryEndpointItem = (item: DeliveryEndpointItem) => {
        setDeliveryEndpointItems((prevState) => [
            ...prevState,
            {
                id: item.id,
                name: item.name,
                type: DeliveryEndpointType.Signiant,
                details: item.details,
                updatedDate: new Date().toISOString(),
                isModified: true
            }
        ]);
    };

    return (
        <DeliveryEndpointContext.Provider
            value={{
                deliveryEndpointItems,
                isLoading,
                addSmartjogDeliveryEndpointItem,
                updateSmartjogDeliveryEndpointItem,
                addAsperaDeliveryEndpointItem,
                updateAsperaDeliveryEndpointItem,
                addSigniantDeliveryEndpointItem,
                updateSigniantDeliveryEndpointItem
            }}
        >
            {children}
        </DeliveryEndpointContext.Provider>
    );
};
